import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  deleteAdminEmail,
  fetchAdminEmail,
  fetchAdminEmails,
  markAdminEmailRead,
  sendAdminEmail,
} from "@/features/adminEmail/adminEmailApi";
import cls from "./AdminEmailCenter.module.scss";

const TABS = [
  { key: "inbox", label: "Inbox", icon: "📥" },
  { key: "sent", label: "Sent", icon: "📤" },
  { key: "system", label: "System Emails", icon: "⚙️" },
  { key: "failed", label: "Failed", icon: "⚠️" },
];

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const addresses = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  return value || "—";
};

const getReplyAddress = (email) => {
  const raw = email?.replyTo || email?.from || "";
  const match = raw.match(/<([^>]+)>/);
  return (match ? match[1] : raw).trim();
};

const getInitial = (email) => {
  const source =
    email?.direction === "inbound"
      ? email?.from
      : Array.isArray(email?.to)
        ? email.to[0]
        : email?.to;

  const clean = String(source || "")
    .replace(/<.*?>/g, "")
    .replace(/"/g, "")
    .trim();

  return clean.charAt(0).toUpperCase() || "M";
};

const getReadableName = (email) => {
  const source =
    email?.direction === "inbound"
      ? email?.from
      : Array.isArray(email?.to)
        ? email.to[0]
        : email?.to;

  const clean = String(source || "")
    .replace(/<.*?>/g, "")
    .replace(/"/g, "")
    .trim();

  return clean || "Email";
};

const getStatusClass = (status = "") => {
  const value = String(status).toLowerCase();

  if (value.includes("fail") || value.includes("error")) return cls.failedBadge;
  if (value.includes("sent") || value.includes("deliver")) return cls.sentBadge;
  if (value.includes("system")) return cls.systemBadge;
  if (value.includes("received") || value.includes("inbound")) {
    return cls.inboxBadge;
  }

  return cls.neutralBadge;
};

const isInboundUnread = (email) =>
  email?.direction === "inbound" && !email?.readAt;

const stripMarkdownStars = (text = "") =>
  String(text).replace(/\*([^*\n]+)\*/g, "$1");

const stripHtml = (value = "") =>
  String(value)
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();

const isEmailHeaderLine = (line = "") => {
  const value = line.trim();

  return (
    /^From:/i.test(value) ||
    /^Sent:/i.test(value) ||
    /^To:/i.test(value) ||
    /^Subject:/i.test(value) ||
    /^Date:/i.test(value) ||
    /^Cc:/i.test(value) ||
    /^Bcc:/i.test(value)
  );
};

const isSeparatorLine = (line = "") => {
  const value = line.trim();

  return (
    /^-{2,}\s*Original message\s*-{2,}$/i.test(value) ||
    /^-{2,}\s*Forwarded message\s*-{2,}$/i.test(value) ||
    /^-{2,}$/.test(value)
  );
};

const cleanTextBlock = (text = "") => {
  return stripMarkdownStars(text)
    .replace(/^\s*>+\s?/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const cleanEmailText = (text = "") => {
  if (!text) return "";

  const lines = String(text).split("\n");
  const cleanedLines = [];

  for (const rawLine of lines) {
    const line = rawLine.trim();

    if (line.startsWith(">")) continue;
    if (/^On .+ wrote:$/i.test(line)) break;
    if (isEmailHeaderLine(line)) continue;
    if (isSeparatorLine(line)) continue;

    cleanedLines.push(rawLine);
  }

  return cleanTextBlock(cleanedLines.join("\n"));
};

const extractQuotedReplyText = (text = "") => {
  if (!text) return "";

  const lines = String(text).split("\n");
  const quotedLines = [];
  let foundQuote = false;

  for (const rawLine of lines) {
    const trimmed = rawLine.trim();

    if (/^On .+ wrote:$/i.test(trimmed)) {
      foundQuote = true;
      continue;
    }

    if (trimmed.startsWith(">")) {
      foundQuote = true;

      const cleanedLine = cleanTextBlock(trimmed.replace(/^>+\s?/g, ""));

      if (!cleanedLine) continue;
      if (isSeparatorLine(cleanedLine)) continue;
      if (/^On .+ wrote:$/i.test(cleanedLine)) continue;
      if (isEmailHeaderLine(cleanedLine)) continue;

      quotedLines.push(cleanedLine);
      continue;
    }

    if (foundQuote && trimmed) {
      const cleanedLine = cleanTextBlock(trimmed);

      if (!cleanedLine) continue;
      if (isSeparatorLine(cleanedLine)) continue;
      if (/^On .+ wrote:$/i.test(cleanedLine)) continue;
      if (isEmailHeaderLine(cleanedLine)) continue;

      quotedLines.push(cleanedLine);
    }
  }

  return cleanTextBlock(quotedLines.join("\n"));
};

const getRawBody = (email) =>
  email?.textBody || stripHtml(email?.htmlBody || "");

const getRepliedMessage = (email) => cleanEmailText(getRawBody(email));

const getOriginalMessage = (email) => extractQuotedReplyText(getRawBody(email));

export default function AdminEmailCenter() {
  const [tab, setTab] = useState("inbox");
  const [emails, setEmails] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);

  const emptyCompose = {
    to: "",
    recipientName: "",
    subject: "",
    textBody: "",
  };

  const [compose, setCompose] = useState(emptyCompose);

  const query = useMemo(
    () => ({
      folder: tab,
      search: search.trim(),
      username: username.trim(),
      page,
      limit: 15,
    }),
    [page, search, tab, username],
  );

  const activeTab = TABS.find((item) => item.key === tab);

  const unreadCount = useMemo(
    () => emails.filter((email) => isInboundUnread(email)).length,
    [emails],
  );

  const failedCount = useMemo(
    () =>
      emails.filter((email) =>
        String(email.status || "")
          .toLowerCase()
          .includes("fail"),
      ).length,
    [emails],
  );

  const loadEmails = useCallback(async () => {
    try {
      setLoading(true);

      const data = await fetchAdminEmails(query);
      const nextEmails = data.emails || [];

      setEmails(nextEmails);
      setPagination(data.pagination || { page, totalPages: 1 });
    } catch (err) {
      console.error(err);
      toast.error("Unable to load admin emails.");
    } finally {
      setLoading(false);
    }
  }, [page, query]);

  useEffect(() => {
    loadEmails();
  }, [loadEmails]);

  useEffect(() => {
    if (!selectedId) {
      setSelectedEmail(null);
      return;
    }

    let cancelled = false;

    const loadDetail = async () => {
      try {
        setDetailLoading(true);

        const data = await fetchAdminEmail(selectedId);
        const email = data.email || null;

        if (cancelled) return;

        setSelectedEmail(email);

        if (isInboundUnread(email)) {
          try {
            const readData = await markAdminEmailRead(email._id);

            if (cancelled) return;

            setSelectedEmail(readData.email);

            setEmails((prev) =>
              prev.map((item) =>
                item._id === readData.email._id ? readData.email : item,
              ),
            );
          } catch (readErr) {
            console.error("Silent mark read failed:", readErr);
          }
        }
      } catch (err) {
        console.error(err);

        if (!cancelled) {
          setSelectedEmail(null);
          toast.error("Unable to load email details.");
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    };

    loadDetail();

    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const switchTab = (nextTab) => {
    setTab(nextTab);
    setPage(1);
    setSelectedId(null);
    setSelectedEmail(null);
  };

  const clearFilters = () => {
    setSearch("");
    setUsername("");
    setPage(1);
    setSelectedId(null);
    setSelectedEmail(null);
  };

  const closeDetail = () => {
    setSelectedId(null);
    setSelectedEmail(null);
  };

  const handleSend = async (event) => {
    event.preventDefault();

    try {
      setSending(true);

      const data = await sendAdminEmail(compose);

      toast.success("Email sent.");
      setCompose(emptyCompose);
      setComposeOpen(false);
      setTab("sent");
      setPage(1);
      setSelectedId(data.email?._id || null);

      await loadEmails();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Email failed to send.");
    } finally {
      setSending(false);
    }
  };

  const handleMarkRead = async () => {
    if (!selectedEmail?._id) return;

    try {
      const data = await markAdminEmailRead(selectedEmail._id);

      setSelectedEmail(data.email);

      setEmails((prev) =>
        prev.map((email) =>
          email._id === data.email._id ? data.email : email,
        ),
      );
    } catch (err) {
      console.error(err);
      toast.error("Unable to mark email as read.");
    }
  };

  const handleReply = () => {
    if (!selectedEmail) return;

    setCompose({
      ...emptyCompose,
      to: getReplyAddress(selectedEmail),
      subject: selectedEmail.subject?.startsWith("Re:")
        ? selectedEmail.subject
        : `Re: ${selectedEmail.subject || ""}`,
      textBody: "",
    });

    setComposeOpen(true);

    window.requestAnimationFrame(() => {
      document
        .getElementById("admin-email-compose")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleDelete = async () => {
    if (!selectedEmail?._id) return;

    const confirmed = window.confirm(
      "Delete this email from the admin email center?",
    );

    if (!confirmed) return;

    try {
      await deleteAdminEmail(selectedEmail._id);

      toast.success("Email deleted.");
      closeDetail();

      await loadEmails();
    } catch (err) {
      console.error(err);
      toast.error("Unable to delete email.");
    }
  };

  return (
    <main className={cls.page}>
      <section className={cls.hero}>
        <div className={cls.heroGlow} aria-hidden="true" />

        <div className={cls.heroText}>
          <p className={cls.eyebrow}>Mailgun • mail.notyu.com</p>
          <h1>Admin Email Center</h1>
        </div>

        <div className={cls.heroStats}>
          <div>
            <span>Page Emails</span>
            <strong>{emails.length}</strong>
          </div>

          <div>
            <span>Unread</span>
            <strong>{unreadCount}</strong>
          </div>

          <div>
            <span>Failed</span>
            <strong>{failedCount}</strong>
          </div>
        </div>

        <div className={cls.heroActions}>
          <button
            type="button"
            className={cls.primaryButton}
            onClick={() => setComposeOpen((prev) => !prev)}
          >
            {composeOpen ? "Close Compose" : "Compose Email"}
          </button>

          <button
            type="button"
            className={cls.ghostButton}
            onClick={loadEmails}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </section>

      {composeOpen ? (
        <section className={cls.composeCard} id="admin-email-compose">
          <div className={cls.cardHeader}>
            <div>
              <p className={cls.eyebrow}>Secure Outbound Mail</p>
              <h2>Compose Email</h2>
              <p>
                Admin emails are sent through backend Mailgun. Keys stay secure
                on the server.
              </p>
            </div>

            <button
              type="button"
              className={cls.closeComposeButton}
              onClick={() => setComposeOpen(false)}
              aria-label="Close compose"
            >
              ✕
            </button>
          </div>

          <form className={cls.composeForm} onSubmit={handleSend}>
            <label>
              <span>To</span>
              <input
                type="email"
                value={compose.to}
                onChange={(event) =>
                  setCompose((prev) => ({ ...prev, to: event.target.value }))
                }
                placeholder="member@example.com"
                required
              />
            </label>

            <label>
              <span>Name (optional)</span>
              <input
                value={compose.recipientName}
                onChange={(event) =>
                  setCompose((prev) => ({
                    ...prev,
                    recipientName: event.target.value,
                  }))
                }
                placeholder="Use username or there if blank"
              />
            </label>

            <label className={cls.fullWidth}>
              <span>Subject</span>
              <input
                value={compose.subject}
                onChange={(event) =>
                  setCompose((prev) => ({
                    ...prev,
                    subject: event.target.value,
                  }))
                }
                placeholder="Email subject"
                required
              />
            </label>

            <label className={cls.fullWidth}>
              <span>Message</span>
              <textarea
                value={compose.textBody}
                onChange={(event) =>
                  setCompose((prev) => ({
                    ...prev,
                    textBody: event.target.value,
                  }))
                }
                placeholder="Write your message..."
                rows={7}
                required
              />
            </label>

            <div className={cls.composeFooter}>
              <p>From: admin@mail.notyu.com</p>

              <button
                className={cls.primaryButton}
                type="submit"
                disabled={sending}
              >
                {sending ? "Sending..." : "Send Email"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      <section className={cls.mailShell}>
        {!selectedId ? (
          <>
            <div className={cls.mailTop}>
              <div>
                <p className={cls.eyebrow}>Message Console</p>
                <h2>{activeTab?.label || "Emails"}</h2>
                <p>
                  {emails.length} emails on this page
                  {unreadCount ? ` • ${unreadCount} unread` : ""}
                </p>
              </div>

              <div className={cls.webhookBox}>
                <span>Inbound Webhook</span>
                <strong>/api/v1/admin/webhooks/mailgun/inbound</strong>
              </div>
            </div>

            <div className={cls.tabs}>
              {TABS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`${cls.tabButton} ${
                    tab === item.key ? cls.activeTab : ""
                  }`}
                  onClick={() => switchTab(item.key)}
                >
                  <span>{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>

            <div className={cls.filters}>
              <label>
                <span>Search</span>
                <input
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(1);
                  }}
                  placeholder="Search email, subject, or body"
                />
              </label>

              <label>
                <span>User</span>
                <input
                  value={username}
                  onChange={(event) => {
                    setUsername(event.target.value);
                    setPage(1);
                  }}
                  placeholder="Filter username"
                />
              </label>

              <button
                type="button"
                className={cls.clearButton}
                onClick={clearFilters}
              >
                Clear
              </button>
            </div>

            <div className={cls.fullInbox}>
              {loading ? (
                <div className={cls.stateBox}>
                  <span className={cls.loader} />
                  <p>Loading emails...</p>
                </div>
              ) : null}

              {!loading && !emails.length ? (
                <div className={cls.stateBox}>
                  <span className={cls.emptyIcon}>✉️</span>
                  <h3>No emails found</h3>
                  <p>Try another folder or remove your filters.</p>
                </div>
              ) : null}

              {!loading &&
                emails.map((email) => (
                  <button
                    key={email._id}
                    type="button"
                    className={`${cls.emailRow} ${
                      isInboundUnread(email) ? cls.unreadRow : ""
                    }`}
                    onClick={() => setSelectedId(email._id)}
                  >
                    <span className={cls.avatar}>{getInitial(email)}</span>

                    <span className={cls.emailMain}>
                      <span className={cls.rowTop}>
                        <strong>
                          {email.direction === "inbound"
                            ? email.from
                            : addresses(email.to)}
                        </strong>
                        <em>{formatDate(email.createdAt)}</em>
                      </span>

                      <span className={cls.subject}>
                        {email.subject || "(No subject)"}
                      </span>

                      <span className={cls.preview}>
                        {getRepliedMessage(email) || "No body"}
                      </span>

                      <span className={cls.badges}>
                        <small className={getStatusClass(email.status)}>
                          {email.status || "unknown"}
                        </small>

                        {email.relatedUser?.username ? (
                          <small>@{email.relatedUser.username}</small>
                        ) : null}

                        {isInboundUnread(email) ? (
                          <small className={cls.unreadBadge}>Unread</small>
                        ) : null}
                      </span>
                    </span>

                    <span className={cls.openHint}>Open</span>
                  </button>
                ))}
            </div>

            <div className={cls.pagination}>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              >
                Previous
              </button>

              <span>
                Page {pagination.page || page} of {pagination.totalPages || 1}
              </span>

              <button
                type="button"
                disabled={page >= (pagination.totalPages || 1)}
                onClick={() => setPage((prev) => prev + 1)}
              >
                Next
              </button>
            </div>
          </>
        ) : (
          <article className={cls.detailPane}>
            <div className={cls.detailTopBar}>
              <button
                type="button"
                className={cls.backButton}
                onClick={closeDetail}
              >
                ← Back to {activeTab?.label || "Emails"}
              </button>

              <button
                type="button"
                className={cls.ghostButton}
                onClick={loadEmails}
                disabled={loading}
              >
                Refresh
              </button>
            </div>

            {detailLoading ? (
              <div className={cls.stateBox}>
                <span className={cls.loader} />
                <p>Loading email details...</p>
              </div>
            ) : null}

            {!detailLoading && !selectedEmail ? (
              <div className={cls.stateBox}>
                <span className={cls.emptyIcon}>📨</span>
                <h3>Email not found</h3>
                <p>This email may have been deleted or moved.</p>
              </div>
            ) : null}

            {!detailLoading && selectedEmail ? (
              <>
                <div className={cls.detailHeader}>
                  <div>
                    <p className={cls.eyebrow}>
                      {selectedEmail.direction || "email"} •{" "}
                      {selectedEmail.status || "unknown"}
                    </p>

                    <h2>{selectedEmail.subject || "(No subject)"}</h2>

                    {selectedEmail.relatedUser ? (
                      <p className={cls.relatedUser}>
                        Related user: @{selectedEmail.relatedUser.username} (
                        {selectedEmail.relatedUser.email})
                      </p>
                    ) : null}
                  </div>

                  <div className={cls.detailActions}>
                    {selectedEmail.direction === "inbound" &&
                    !selectedEmail.readAt ? (
                      <button type="button" onClick={handleMarkRead}>
                        Mark Read
                      </button>
                    ) : null}

                    <button type="button" onClick={handleReply}>
                      Reply
                    </button>

                    <button
                      type="button"
                      className={cls.dangerButton}
                      onClick={handleDelete}
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {selectedEmail.errorMessage ? (
                  <div className={cls.errorBox}>
                    <strong>Delivery Error</strong>
                    <p>{selectedEmail.errorMessage}</p>
                  </div>
                ) : null}

                <div className={cls.conversationView}>
                  <section className={cls.repliedMessageCard}>
                    <div className={cls.emailBodyHeader}>
                      <div className={cls.bodyAvatar}>
                        {getInitial(selectedEmail)}
                      </div>

                      <div>
                        <strong>Replied message</strong>
                        <span>
                          From {getReadableName(selectedEmail)} •{" "}
                          {formatDate(selectedEmail.createdAt)}
                        </span>
                      </div>
                    </div>

                    <div className={cls.emailBodyBubble}>
                      <pre className={cls.bodyText}>
                        {getRepliedMessage(selectedEmail) ||
                          "No replied message body."}
                      </pre>
                    </div>
                  </section>

                  <section className={cls.originalMessageCard}>
                    <div className={cls.originalMessageHeader}>
                      <span>↩</span>

                      <div>
                        <strong>Original message</strong>
                        <p>This is the message the user replied to.</p>
                      </div>
                    </div>

                    <pre className={cls.originalMessageText}>
                      {getOriginalMessage(selectedEmail) ||
                        "No original message found in this email."}
                    </pre>
                  </section>
                </div>

                {selectedEmail.htmlBody ? (
                  <details className={cls.htmlDetails}>
                    <summary>Preview original HTML body</summary>
                    <iframe
                      title="Email HTML body"
                      srcDoc={selectedEmail.htmlBody}
                      sandbox=""
                    />
                  </details>
                ) : null}

                <dl className={cls.metaGrid}>
                  <div>
                    <dt>From</dt>
                    <dd>{selectedEmail.from || "—"}</dd>
                  </div>

                  <div>
                    <dt>To</dt>
                    <dd>{addresses(selectedEmail.to)}</dd>
                  </div>

                  <div>
                    <dt>Reply-To</dt>
                    <dd>{selectedEmail.replyTo || "—"}</dd>
                  </div>

                  <div>
                    <dt>Date</dt>
                    <dd>{formatDate(selectedEmail.createdAt)}</dd>
                  </div>

                  <div>
                    <dt>Mailgun ID</dt>
                    <dd>{selectedEmail.mailgunMessageId || "—"}</dd>
                  </div>

                  <div>
                    <dt>Read</dt>
                    <dd>
                      {selectedEmail.readAt
                        ? formatDate(selectedEmail.readAt)
                        : "Unread"}
                    </dd>
                  </div>
                </dl>
              </>
            ) : null}
          </article>
        )}
      </section>
    </main>
  );
}
